import emailjs from '@emailjs/browser';

// EmailJS configuration
const EMAILJS_CONFIG = {
  SERVICE_ID: import.meta.env.VITE_EMAILJS_SERVICE_ID,
  TEMPLATE_ID: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
  PUBLIC_KEY: import.meta.env.VITE_EMAILJS_PUBLIC_KEY
};

export interface EmailData {
  name: string;
  email: string;
  phone: string;
  city: string;
  message: string;
  to_name?: string;
  subject?: string;
  /** Lead origin for CRM segmentation (stored as utmSource in Leadflow v2).
   *  Does NOT affect the lead's visibility or badge. Defaults to 'website-contact'. */
  source?: string;
}

/**
 * Send data to Leadflow CRM
 */
const sendToLeadflow = async (data: EmailData): Promise<boolean> => {
  try {
    const response = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const receipt: unknown = await response.json();
    return response.ok && typeof receipt === 'object' && receipt !== null &&
      'success' in receipt && receipt.success === true;
  } catch { return false; }
};
/**
 * Send email via EmailJS
 */
const sendViaEmailJS = async (data: EmailData): Promise<boolean> => {
  try {
    // Check if PUBLIC_KEY is available
    if (!EMAILJS_CONFIG.PUBLIC_KEY) {
      // console.error('EmailJS PUBLIC_KEY is missing');
      return false;
    }
    
    // Initialize EmailJS safely
    try {
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
    } catch {
      // console.error('EmailJS initialization failed:', initError);
      return false;
    }
    
    // Check if other required config is available
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID) {
      // console.error('EmailJS SERVICE_ID or TEMPLATE_ID is missing');
      return false;
    }
    
    const response = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      {
        from_name: data.name,
        from_email: data.email,
        phone: data.phone,
        city: data.city,
        message: data.message,
        to_name: data.to_name || 'StayCool Airco',
        reply_to: data.email,
        subject: data.subject || 'Nieuwe aanvraag via website'
      }
    );

    if (response.status !== 200) {
      // console.error('EmailJS responded with non-200 status:', response);
      return false;
    }
    
    // console.log('EmailJS submission successful');
    return true;
  } catch {
    // console.error('EmailJS submission failed:', error);
    return false;
  }
};

/**
 * Send contact form data using EmailJS and Leadflow CRM
 */
export const sendEmail = async (data: EmailData): Promise<void> => {
  const received = await sendToLeadflow(data);
  if (!received) throw new Error('Uw aanvraag kon niet worden bevestigd. Neem contact met ons op voordat u opnieuw verzendt.');
  // Notification is secondary; it cannot turn a failed CRM delivery into success.
  await sendViaEmailJS(data);
};
